/**
 * Shared API Client
 *
 * Generic HTTP client for making API requests with error handling,
 * timeout management, and authentication support. Designed to be
 * reused across different service modules.
 *
 * @module services/shared/ApiClient
 */

import { BaseApiError } from './BaseApiError';

/**
 * Request interceptor type
 */
export type RequestInterceptor = (
  endpoint: string,
  options: RequestInit
) => Promise<RequestInit>;

/**
 * Response interceptor type
 */
export type ResponseInterceptor = (
  response: Response
) => Promise<Response>;

/**
 * Get the appropriate API base URL based on environment and hosting mode
 */
export function getApiBaseUrl(): string {
  // Check if we're in unified hosting mode (same origin)
  const isUnifiedHosting = import.meta.env.VITE_UNIFIED_HOSTING === 'true';

  if (isUnifiedHosting) {
    // Use relative paths for unified hosting (same origin)
    return '/api';
  }

  // Use environment variable, defaulting to a relative path (same-origin).
  // NOTE: do not default to localhost — a baked localhost value breaks
  // deployed builds. Local dev sets VITE_API_BASE_URL explicitly in .env.
  return import.meta.env.VITE_API_BASE_URL || '/api';
}

/**
 * API Client configuration options
 */
export interface ApiClientConfig {
  /** Base URL for API requests */
  baseUrl?: string;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Default headers for all requests */
  defaultHeaders?: Record<string, string>;
}

/**
 * API Error Response structure
 */
export interface ApiErrorResponse {
  error?: {
    message?: string;
    code?: string;
    details?: Record<string, string>;
  };
  detail?: string | ValidationDetailEntry[];
}

export interface ValidationDetailEntry { loc?: Array<string | number>; msg: string; type?: string }

/** FastAPI / Pydantic 422 `detail` arrays → one readable line per error and a field map (design §4.13.6).
 *  Drops a leading `query` as well as `body`, as §4.13.6 specifies, so URL-parameter 422s read the same way. */
export function formatValidationDetail(detail: ValidationDetailEntry[]): { message: string; fieldErrors: Record<string, string> } {
  const fieldErrors: Record<string, string> = {};
  const lines = detail.map((entry) => {
    const path = (entry.loc ?? []).filter((p, i) => !(i === 0 && (p === 'body' || p === 'query'))).map(String).join('.');
    if (path && !(path in fieldErrors)) fieldErrors[path] = entry.msg;
    return path ? `${path}: ${entry.msg}` : entry.msg;
  });
  return { message: lines.join('\n') || 'Validation failed', fieldErrors };
}

/**
 * Generic API Client with configurable error handling
 */
export class ApiClient<TError extends BaseApiError> {
  private static globalRequestInterceptors: RequestInterceptor[] = [];
  private static globalResponseInterceptors: ResponseInterceptor[] = [];
  private baseUrl: string;
  private timeout: number;
  private defaultHeaders: Record<string, string>;
  private errorFactory: (message: string, status?: number, fieldErrors?: Record<string, string>, code?: string) => TError;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];

  constructor(
    errorFactory: (message: string, status?: number, fieldErrors?: Record<string, string>, code?: string) => TError,
    config: ApiClientConfig = {}
  ) {
    this.baseUrl = config.baseUrl || getApiBaseUrl();
    this.timeout = config.timeout || 600000;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...config.defaultHeaders
    };
    this.errorFactory = errorFactory;
  }

  /**
   * Set authorization token for authenticated requests
   */
  setAuthToken(token: string): void {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Remove authorization token
   */
  clearAuthToken(): void {
    delete this.defaultHeaders['Authorization'];
  }

  /**
   * Update default headers
   */
  setHeaders(headers: Record<string, string>): void {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
  }

  /**
   * Get current configuration
   */
  getConfig(): { baseUrl: string; timeout: number; headers: Record<string, string> } {
    return {
      baseUrl: this.baseUrl,
      timeout: this.timeout,
      headers: { ...this.defaultHeaders }
    };
  }

  /**
   * Add request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Register a global request interceptor applied to all ApiClient instances.
   */
  static addGlobalRequestInterceptor(interceptor: RequestInterceptor): void {
    ApiClient.globalRequestInterceptors.push(interceptor);
  }

  /**
   * Register a global response interceptor applied to all ApiClient instances.
   */
  static addGlobalResponseInterceptor(interceptor: ResponseInterceptor): void {
    ApiClient.globalResponseInterceptors.push(interceptor);
  }

  /**
   * Clear all global interceptors (primarily for testing).
   */
  static clearGlobalInterceptors(): void {
    ApiClient.globalRequestInterceptors = [];
    ApiClient.globalResponseInterceptors = [];
  }

  /**
   * Make HTTP request with error handling and interceptors
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Apply request interceptors
    let modifiedOptions: RequestInit = {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...options.headers
      }
    };

    const requestInterceptors = [
      ...ApiClient.globalRequestInterceptors,
      ...this.requestInterceptors,
    ];

    for (const interceptor of requestInterceptors) {
      modifiedOptions = await interceptor(endpoint, modifiedOptions);
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      let response = await fetch(url, {
        ...modifiedOptions,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Apply response interceptors
      const responseInterceptors = [
        ...ApiClient.globalResponseInterceptors,
        ...this.responseInterceptors,
      ];

      for (const interceptor of responseInterceptors) {
        response = await interceptor(response);
      }

      // Handle HTTP errors
      if (!response.ok) {
        await this.handleHttpError(response);
      }

      // Parse JSON response only if there's content and T is not void
      const contentLength = response.headers.get('content-length');
      const hasContent = contentLength && parseInt(contentLength) > 0;

      // Check if response has content or if T is not void
      if (hasContent || (response.status !== 204 && response.headers.get('content-type')?.includes('application/json'))) {
        try {
          const data = await response.json();
          return data as T;
        } catch (error) {
          // If JSON parsing fails but we expected content, handle gracefully
          if (hasContent) {
            throw error;
          }
          // Return undefined for void responses that failed JSON parsing
          return undefined as T;
        }
      }

      // Return undefined for void responses (204 No Content, empty responses)
      return undefined as T;

    } catch (error) {
      clearTimeout(timeoutId);

      // Handle network errors
      if (error instanceof Error) {
        const errorMessage =
          typeof (error as unknown as { message?: unknown })?.message === 'string'
            ? (error as unknown as { message: string }).message
            : '';

        if (error.name === 'AbortError') {
          throw this.errorFactory(
            'Request timeout. Please check your connection and try again.',
            undefined,
            undefined,
            'TIMEOUT'
          );
        }

        if (errorMessage.includes('Failed to fetch')) {
          throw this.errorFactory(
            'Network error. Please check your connection and try again.',
            undefined,
            undefined,
            'NETWORK_ERROR'
          );
        }
      }

      // Re-throw API errors
      if (error instanceof BaseApiError) {
        throw error;
      }

      // Handle unexpected errors
      throw this.errorFactory(
        'An unexpected error occurred. Please try again.',
        undefined,
        undefined,
        'UNKNOWN_ERROR'
      );
    }
  }

  /**
   * Handle HTTP error responses
   */
  private async handleHttpError(response: Response): Promise<never> {
    const mfaHeader = response.headers.get('x-mfa-required');
    if (response.status === 403 && mfaHeader?.toLowerCase() === 'true') {
      // Inform MFA store without creating a hard dependency loop
      try {
        const { useMfaStore } = await import('@/stores/mfaStore');
        useMfaStore.getState().setMfaRequired(true);
      } catch (e) {
        // Best-effort; avoid breaking flow if store is unavailable
        console.warn('Failed to set MFA required flag', e);
      }

      throw this.errorFactory('MFA required', response.status, undefined, 'MFA_REQUIRED');
    }

    let errorData: ApiErrorResponse & { message?: string; fieldErrors?: Record<string, string> };

    try {
      errorData = await response.json();
    } catch {
      // If we can't parse the error response, create a generic error
      const statusText = response.statusText || 'Unknown Error';

      // Handle specific status codes with expected messages
      if (response.status === 401) {
        throw this.errorFactory('Unauthorized access', response.status, undefined, 'UNAUTHORIZED');
      }
      if (response.status === 403) {
        throw this.errorFactory('Insufficient permissions', response.status, undefined, 'FORBIDDEN');
      }
      if (response.status === 429) {
        throw this.errorFactory('Rate limit exceeded', response.status, undefined, 'RATE_LIMIT');
      }

      throw this.errorFactory(
        `HTTP ${response.status}: ${statusText}`,
        response.status,
        undefined,
        `HTTP_${response.status}`
      );
    }

    // Handle different error response formats
    let message: string;
    let fieldErrors: Record<string, string> | undefined;
    let code: string | undefined;

    // Format 1: { error: { message, code, details } }
    if (errorData.error) {
      message = errorData.error.message || 'An error occurred';
      code = errorData.error.code;
      fieldErrors = errorData.error.details;
    }
    // Format 2: { message, fieldErrors } (test format)
    else if (errorData.message) {
      message = errorData.message;
      fieldErrors = errorData.fieldErrors;
    }
    // Format 3: { detail } (Django format)
    else if (errorData.detail) {
      if (Array.isArray(errorData.detail)) {
        ({ message, fieldErrors } = formatValidationDetail(errorData.detail));
      } else {
        message = errorData.detail;
      }
    }
    // Default fallback
    else {
      message = 'An error occurred';
    }

    // Handle specific status codes with expected messages
    if (response.status === 401) {
      message = 'Unauthorized access';
      code = 'UNAUTHORIZED';
    } else if (response.status === 403) {
      message = 'Insufficient permissions';
      code = 'FORBIDDEN';
    } else if (response.status === 429) {
      message = 'Rate limit exceeded';
      code = 'RATE_LIMIT';
    }

    throw this.errorFactory(message, response.status, fieldErrors, code);
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      headers
    });
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    data?: unknown,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      headers,
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    data?: unknown,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      headers,
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    data?: unknown,
    headers?: Record<string, string>
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      headers,
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      headers
    });
  }

  /**
   * Upload file with FormData
   */
  async upload<T>(
    endpoint: string,
    formData: FormData,
    headers?: Record<string, string>
  ): Promise<T> {
    // Remove Content-Type header for FormData uploads (browser sets it automatically)
    const uploadHeaders = { ...this.defaultHeaders, ...headers };
    delete uploadHeaders['Content-Type'];

    return this.request<T>(endpoint, {
      method: 'POST',
      headers: uploadHeaders,
      body: formData
    });
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    return this.get('/health');
  }
}